import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { ISP_COLORS_VAR, ISP_TYPOGRAPHY_VAR } from 'common/colors';

import {
  CostFormattingOptions,
  FormattingOptions,
  FractionOptions,
  PercentFormattingOptions,
  SuffixOptions,
} from './model';

const COLORS_VARIABLES: Record<
  Exclude<Required<FormattingOptions>['color'], 'inherit'>,
  string
> = {
  grey: ISP_COLORS_VAR.textAdditional,
  main: ISP_COLORS_VAR.textMain,
  green: ISP_COLORS_VAR.textSuccess,
  blue: ISP_COLORS_VAR.textBlue,
  red: ISP_COLORS_VAR.textDanger,
  cyan: ISP_COLORS_VAR.textCyan,
  yellow: ISP_COLORS_VAR.textWarning,
};

const SIGN_MAP: Record<
  FormattingOptions['sign'],
  { positive: string; negative: string }
> = {
  auto: { negative: '-', positive: '' },
  force: { negative: '-', positive: '+' },
  invert: { negative: '', positive: '-' },
  negative: { negative: '-', positive: '-' },
  positive: { negative: '+', positive: '+' },
};

/**
 * Get formatted integer part of passed value
 *
 * @param value
 */
function getFormattedIntegerPart(value: string): string {
  if (!value) {
    return '';
  }

  const valueNum = Number(value);
  const localeFormattedValue = Math.abs(valueNum).toLocaleString('ru-RU');
  const formattedIntegerPart = localeFormattedValue.split(/[,.]/)[0];

  return formattedIntegerPart.replace(/\s/g, '&nbsp;');
}

/**
 * Discard minor zeros in fraction part
 *
 * @param fractionPart
 */
function trimMinorZeros(fractionPart: string): string {
  if (!fractionPart) {
    return '';
  }

  return fractionPart.replace(/0+$/, '');
}

/**
 * Get formatted fraction part of passed value
 *
 * @param value
 * @param options - options that affect formatting of fraction part
 */
function getFormattedFractionPart(
  value: string,
  options: FractionOptions,
): string {
  if (!value || options.fraction === 'none') {
    return '';
  }

  let fractionPart = value.split(/[,.]/)[1] ?? '';
  if (options.fraction === 'trimZeros') {
    fractionPart = trimMinorZeros(fractionPart);
  }
  if (!fractionPart) {
    return '';
  }

  return options.smallFraction
    ? `.<small>${fractionPart}</small>`
    : `.${fractionPart}`;
}

/**
 * Get only the formatted value
 *
 * @param value
 * @param options - options that affect formatting of fraction part
 */
function getFormattedOnlyValue(
  value: string,
  options: FractionOptions,
): string {
  const integerFormatted = getFormattedIntegerPart(value);
  const fractionFormatted = getFormattedFractionPart(value, options);
  return `${integerFormatted}${fractionFormatted}`;
}

/**
 * Get sign string to insert before value
 *
 * @param sign
 * @param isValueNegative
 */
function getSignString(
  sign: FormattingOptions['sign'],
  isValueNegative: boolean,
): string {
  const signStrings = SIGN_MAP[sign];
  return isValueNegative ? signStrings.negative : signStrings.positive;
}

/**
 * Append sign string before provided value if exists
 *
 * @param value
 * @param sign
 * @param isValueNegative
 */
function appendSign(
  value: string,
  sign: FormattingOptions['sign'],
  isValueNegative: boolean,
): string {
  if (!value) {
    return '';
  }

  const signStr = getSignString(sign, isValueNegative);
  if (!signStr) {
    return value;
  }

  return `${signStr}${value}`;
}

/**
 * Add crossed style if value exists
 *
 * @param value
 */
function appendCrossedStyle(value: string): string {
  if (!value) {
    return '';
  }

  return `<span style="text-decoration: line-through;">${value}</span>`;
}

/**
 * Append prefix
 *
 * @param value
 * @param prefix
 */
function appendPrefix(value: string, prefix: string): string {
  if (prefix && value) {
    return `${prefix} ${value}`;
  }

  if (prefix) {
    return prefix;
  }

  return value ?? '';
}

/**
 * Check if there is data for adding a suffix
 *
 * @param options
 */
function isExistSuffix(options: SuffixOptions): boolean {
  return Boolean(options.percent || options.currency || options.period);
}

/**
 * Append suffix
 *
 * @param value
 * @param options - options affecting added suffix
 * @param options.period
 * @param options.percent
 * @param options.currency
 * @param options.sanitize
 */
function appendSuffix(value: string, options: SuffixOptions): string {
  if (!isExistSuffix(options)) {
    return value;
  }

  let result = value;
  const isNeedAddSpace = options.currency || options.percent;
  if (value && isNeedAddSpace) {
    result += '&nbsp;';
  }

  if (options.percent) {
    result += '%';
  } else {
    if (options.currency) {
      // @WARN there can be custom client currencies on BE, potential XSS
      result += `${
        options.sanitize.sanitize(SecurityContext.HTML, options.currency) || ''
      }`;
    }
    if (options.period) {
      const formattedPeriod = options.period.replace(/\s/g, '&nbsp;');
      result += `/${formattedPeriod}`;
    }
  }

  return result;
}

/**
 * Add styles that will apply to all final row
 *
 * @param value
 * @param color
 * @param bold
 */
function appendGeneralStyles(
  value: string,
  color: FormattingOptions['color'],
  bold: boolean,
): string {
  if (!color && !bold) {
    return value;
  }

  let outsideSpan = '<span style="';
  if (color && color !== 'inherit') {
    outsideSpan += `color: var(${COLORS_VARIABLES[color]});`;
  }
  if (bold) {
    outsideSpan += `font-weight: var(${ISP_TYPOGRAPHY_VAR.bold});`;
  }

  return `${outsideSpan}">${value}</span>`;
}

/**
 * Format only free cost, replace 0.00 to msg text
 *
 * @param freeMsg
 * @param period
 */
function formatFreeValue(freeMsg: string, period?: string): string {
  if (!period) {
    return freeMsg;
  }
  const formattedPeriod = period.replace(/\s/g, '&nbsp;');
  return `${freeMsg}/${formattedPeriod}`;
}

/**
 * Format to value string for propper displaying
 *
 * @param value
 * @param sanitize
 * @param options
 */
export function formatValue(
  value: string,
  sanitize: DomSanitizer,
  options: FormattingOptions = { sign: 'auto' },
): string {
  const valueNum = Number(value);
  // check for BE propper value
  if (isNaN(valueNum)) {
    return '-';
  }

  const {
    fraction = 'auto',
    smallFraction = false,
    sign,
    percent,
    currency,
    period,
    crossed,
    color,
    bold = false,
    prefix = '',
    freeMessage,
  } = options;

  const isFree = freeMessage && valueNum === 0;
  if (isFree) {
    return formatFreeValue(freeMessage, period);
  }

  let resultValue = getFormattedOnlyValue(value, {
    fraction,
    smallFraction,
  });
  const isNegative = valueNum < 0;
  resultValue = appendSign(resultValue, sign, isNegative);
  if (crossed) {
    resultValue = appendCrossedStyle(resultValue);
  }
  resultValue = appendSuffix(resultValue, {
    period,
    percent,
    currency,
    sanitize,
  });
  resultValue = appendPrefix(resultValue, prefix);
  resultValue = appendGeneralStyles(resultValue, color, bold);

  return resultValue;
}

/**
 * Format to cost string for propper displaying
 *
 * @param cost
 * @param sanitize
 * @param options
 */
export function formatCost(
  cost: string,
  sanitize: DomSanitizer,
  options?: CostFormattingOptions,
): string {
  const formattingOptions: FormattingOptions = {
    percent: false,
    sign: 'auto',
    ...options,
  };

  return formatValue(cost, sanitize, formattingOptions);
}

/**
 * Format to percent string for propper displaying
 *
 * @param percent
 * @param sanitize
 * @param options
 */
export function formatPercent(
  percent: string,
  sanitize: DomSanitizer,
  options?: PercentFormattingOptions,
): string {
  const formattingOptions: FormattingOptions = {
    sign: 'auto',
    crossed: false,
    fraction: 'trimZeros',
    percent: true,
    smallFraction: false,
    ...options,
  };

  return formatValue(percent, sanitize, formattingOptions);
}
